import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, AlertTriangle, Pill, Bed, TrendingUp, Heart, Activity } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';

const PIE_COLORS = ['#076633', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6'];

export default function Report() {
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/report/kpi').then(r => setKpi(r.data)).catch(() => toast.error('Errore caricamento report')).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" /></div>;

  const stats = [
    { label: 'Ospiti Presenti', value: kpi?.totale_ospiti || 0, icon: Users, color: 'bg-blue-50 text-brand' },
    { label: 'Occupazione', value: `${kpi?.occupazione_percentuale || 0}%`, icon: Bed, color: 'bg-emerald-50 text-emerald-600' },
    { label: 'Terapie Attive', value: kpi?.terapie_attive || 0, icon: Pill, color: 'bg-amber-50 text-amber-600' },
    { label: 'Cadute Mese', value: kpi?.cadute_mese || 0, icon: AlertTriangle, color: 'bg-red-50 text-red-600' },
    { label: 'Lesioni Mese', value: kpi?.lesioni_mese || 0, icon: Heart, color: 'bg-orange-50 text-orange-600' },
    { label: 'Ricoveri Mese', value: kpi?.ricoveri_mese || 0, icon: Activity, color: 'bg-purple-50 text-purple-600' },
  ];

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto" data-testid="report-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel] tracking-tight">Report e KPI</h1>
          <p className="text-sm text-slate-500 mt-0.5">Indicatori di performance della struttura</p>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="report-kpi-grid">
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <Card key={i} className="card-hover">
              <CardContent className="p-4 text-center">
                <div className={`p-2 rounded-lg ${s.color} w-fit mx-auto mb-2`}><Icon className="w-5 h-5" /></div>
                <p className="text-2xl font-bold text-slate-900 font-[Cinzel]">{s.value}</p>
                <p className="text-xs text-slate-500 mt-1">{s.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-brand" />Trend Eventi (6 mesi)</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={kpi?.eventi_trend || []} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="mese" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  <Bar dataKey="eventi" fill="#076633" radius={[4, 4, 0, 0]} name="Eventi" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Heart className="w-4 h-4 text-red-500" />Distribuzione Eventi</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72 flex items-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={kpi?.eventi_per_tipo || []} dataKey="conteggio" nameKey="tipo" cx="50%" cy="50%" outerRadius={100} label={({ tipo, conteggio }) => conteggio > 0 ? `${tipo}: ${conteggio}` : ''} labelLine={false} style={{ fontSize: 11 }}>
                    {(kpi?.eventi_per_tipo || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Riepilogo Mensile</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div className="p-3 rounded-lg bg-slate-50">
              <p className="text-xs text-slate-500">Somministrazioni Oggi</p>
              <p className="text-lg font-bold">{kpi?.somministrazioni_oggi || 0}</p>
            </div>
            <div className="p-3 rounded-lg bg-red-50">
              <p className="text-xs text-slate-500">Mancate Oggi</p>
              <p className="text-lg font-bold text-red-600">{kpi?.somministrazioni_mancate || 0}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50">
              <p className="text-xs text-slate-500">Ospiti Ricoverati</p>
              <p className="text-lg font-bold">{kpi?.ospiti_ricoverati || 0}</p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-50">
              <p className="text-xs text-slate-500">Ospiti Dimessi</p>
              <p className="text-lg font-bold">{kpi?.ospiti_dimessi || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
